import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, from, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';
import { HireSearchService } from 'src/app/hire-person-search/services/hire-search.service';

export type PipelineExportScope = 'all' | 'currentPage' | 'selected';
export type PipelineRevealScope = 'all' | 'currentPage' | 'selected';

/**
 * Bridges candidate selection state between project-details-view (which owns the
 * "select all" checkbox and the bulk "Add to Pipeline" trigger) and whichever routed
 * child (project-search-list, inbound-applications, etc.) is currently rendering the
 * candidate cards. They aren't Angular parent/child via template, only via
 * <router-outlet>, so state can't travel through @Input/@Output — hence this service.
 */
@Injectable({ providedIn: 'root' })
export class CandidateSelectionService {

  constructor(private router: Router, private hireSearchService: HireSearchService) {}
  private candidateIdsSubject = new BehaviorSubject<string[]>([]); // ids currently loaded in the active list
  private selectedIdsSubject = new BehaviorSubject<Set<string>>(new Set());

  candidateIds$ = this.candidateIdsSubject.asObservable();
  selectedIds$ = this.selectedIdsSubject.asObservable();

  // id -> full candidate object for whatever list is currently loaded. Kept separate
  // from candidateIdsSubject/selectedIdsSubject so existing id-only consumers of
  // setCandidateIds/selectedIds$ are completely unaffected.
  private candidatesMap = new Map<string, any>();
  private defaultIdKey = (c: any): string => c?.basicdetails?.candidatenumber ?? c?.id;

  private reloadSource = new Subject<void>();
reload$ = this.reloadSource.asObservable();

private reevaluationIdsSubject = new BehaviorSubject<string[]>([]);
reevaluationIds$ = this.reevaluationIdsSubject.asObservable();

private reevaluationResultsSubject = new Subject<any[]>();
reevaluationResults$ = this.reevaluationResultsSubject.asObservable();

setReevaluationResults(results: any[]) {
  this.reevaluationResultsSubject.next(results);
}

setReevaluationIds(ids: string[]) {
  this.reevaluationIdsSubject.next(ids);
}

getReevaluationIds(): string[] {
  return this.reevaluationIdsSubject.value;
}

triggerReload(): void {
  this.reloadSource.next();
}

private pipelineCandidatesCountSubject = new BehaviorSubject<number>(0);
pipelineCandidatesCount$ = this.pipelineCandidatesCountSubject.asObservable();

private projectCandidatesCountSubject = new BehaviorSubject<number>(0);
projectCandidatesCount$ = this.projectCandidatesCountSubject.asObservable();

/** Called by ProjectPipelineComponent whenever its (possibly filtered) listing loads,
 * so ProjectDetailsViewComponent's header count can stay in sync across the router-outlet
 * boundary - same reasoning as the rest of this service. */
setPipelineCandidatesCount(count: number) {
  this.pipelineCandidatesCountSubject.next(count);
}

/** Optimistically bump the pipeline count by `delta` (e.g. number of candidates
 *  just added via add-to-pipeline) so the sidebar badge updates immediately
 *  without waiting for a full pipeline refetch. */
incrementPipelineCandidatesCount(delta: number) {
  if (delta > 0) {
    this.pipelineCandidatesCountSubject.next(this.pipelineCandidatesCountSubject.value + delta);
  }
}

get pipelineCandidatesCountSnapshot(): number {
  return this.pipelineCandidatesCountSubject.value;
}

/** Set by ProjectDetailsViewComponent once project details load, so ProjectPipelineComponent
 * can build the pipeline export filename client-side without a header round-trip. */
private projectName = '';

setProjectName(name: string): void {
  this.projectName = name || '';
}

get projectNameSnapshot(): string {
  return this.projectName;
}

/**
 * The pipeline export picker (three-dot menu -> "Export" -> modal) lives in
 * ProjectDetailsViewComponent, but the actual export data/logic (pipelineCandidates,
 * filterPayload, the HTTP call) lives in ProjectPipelineComponent - same router-outlet split
 * as the rest of this service, so the chosen scope travels through here.
 */
  private exportRequestedSource = new Subject<PipelineExportScope>();
  exportRequested$ = this.exportRequestedSource.asObservable();

  requestPipelineExport(scope: PipelineExportScope): void {
    this.exportRequestedSource.next(scope);
  }

  private toastSource = new Subject<{ message: string; type: 'success' | 'danger' }>();
  toast$ = this.toastSource.asObservable();

  showToast(message: string, type: 'success' | 'danger' = 'success'): void {
    this.toastSource.next({ message, type });
  }

  get currentTab(): string {
    return this.router.url.split('/')[3] || '';
  }

  get candidateIdsSnapshot(): string[] {
    return this.candidateIdsSubject.value;
  }

  get selectedIdsSnapshot(): Set<string> {
    return this.selectedIdsSubject.value;
  }

  /** Called by the list component whenever it loads/reloads its candidates. */
setCandidateIds(ids: string[]): void {
  this.candidateIdsSubject.next(ids);
  const idSet = new Set(ids);
  const filtered = new Set([...this.selectedIdsSubject.value].filter(id => idSet.has(id)));
  if (filtered.size !== this.selectedIdsSubject.value.size) {
    this.selectedIdsSubject.next(filtered);
  }
}

  /**
   * Same job as setCandidateIds, but for callers that also need the full candidate
   * objects back later (e.g. bulk "Add to Campaign", which needs more than an id).
   * Internally calls setCandidateIds so id-based consumers behave exactly as before.
   */
  setCandidates(candidates: any[], idKey: (c: any) => string = this.defaultIdKey): void {
    this.candidatesMap = new Map(candidates.map(c => [idKey(c), c]));
    this.setCandidateIds(candidates.map(idKey));
  }

  setProjectCandidatesCount(count: number) {
    this.projectCandidatesCountSubject.next(count);
  }

  get projectCandidatesCountSnapshot(): number {
    return this.projectCandidatesCountSubject.value;
  }

  /** Resolves the currently-selected ids to full candidate objects. */
  getSelectedCandidates(): any[] {
    return [...this.selectedIdsSubject.value]
      .map(id => this.candidatesMap.get(id))
      .filter(Boolean);
  }

  /** Resolves an arbitrary id list (e.g. the currently-loaded page) to full candidate objects. */
  getCandidatesByIds(ids: string[]): any[] {
    return (ids || []).map(id => this.candidatesMap.get(id)).filter(Boolean);
  }

  /**
   * Shared elastic-id resolver for candidate objects from either the search-listing shape
   * (top-level elastic_id/id/idinindex) or the pipeline shape (nested under basicdetails).
   */
  resolveElasticId(candidate: any): string {
    return candidate?.basicdetails?.elastic_id || candidate?.elastic_id || candidate?.elasticid || candidate?.idinindex || candidate?.id || '';
  }

  /** Called when an individual candidate checkbox is toggled. */
  toggleCandidate(id: string, checked: boolean): void {
    const updated = new Set(this.selectedIdsSubject.value);
    checked ? updated.add(id) : updated.delete(id);
    this.selectedIdsSubject.next(updated);
  }

  /** Called by the "select all" checkbox. */
  setSelectAll(checked: boolean): void {
    this.selectedIdsSubject.next(checked ? new Set(this.candidateIdsSubject.value) : new Set());
  }

  /** Called after a successful bulk add-to-pipeline, or on route leave. */
  clear(): void {
    this.selectedIdsSubject.next(new Set());
    this.justSubmittedRevealIds.clear();
  }

  /**
 * I don't have your actual candidate-selection.service.ts, so this isn't a full file -
 * it's the exact block to merge into it. It follows the same pattern you're already using
 * for reevaluationIds$ / pipelineCandidatesCount$ / reload$ (BehaviorSubject/Subject +
 * observable + setter), so it should drop in next to those with no other changes needed.
 *
 * Everything below is referenced by the edited files:
 *   - add-to-pipeline-stage.component.ts  -> notifyStageUpdated(), addStageIfMissing()
 *   - project-pipeline.component.ts       -> stageUpdated$, stageList$, setStageList()
 *   - hire-slider.component.ts            -> stageUpdated$, stageList$, setStageList()
 *   - project-details-view.component.ts   -> stageList$
 */

// Make sure these are imported at the top of the service file:
// import { BehaviorSubject, Subject } from 'rxjs';


// --- add as class members, alongside your existing subjects (e.g. reload$) ---

/**
 * Fires whenever one or more candidates' pipeline stage changes, from ANYWHERE
 * (a single card, the slider, or a bulk action). Payload carries the candidate
 * number(s) affected and the new stage name, so any subscriber can patch its own
 * local copy of that candidate in place instead of refetching.
 */
private stageUpdatedSource = new Subject<{ candidatenumbers: string[]; stage: string }>();
stageUpdated$ = this.stageUpdatedSource.asObservable();

notifyStageUpdated(candidatenumbers: string[], stage: string): void {
  if (!candidatenumbers?.length || !stage) return;
  this.stageUpdatedSource.next({ candidatenumbers, stage });
}

/**
 * Shared list of pipeline stages for this project, so every open
 * <app-add-to-pipeline-stage> dropdown (per-card, in the slider, in the bulk
 * actions bar) shows the same, up-to-date set of stages - including ones just
 * created by any one of them - without each having to independently refetch.
 */
private stageListSource = new BehaviorSubject<string[]>([]);
stageList$ = this.stageListSource.asObservable();

setStageList(stageList: string[]): void {
  this.stageListSource.next(stageList || []);
}

/**
 * Called right after a NEW stage is successfully created, so it appears in every
 * dropdown immediately - no need to wait for a getPipelineStage() refetch.
 */
addStageIfMissing(stage: string): void {
  if (!stage) return;
  const current = this.stageListSource.value;
  if (!current.includes(stage)) {
    this.stageListSource.next([...current, stage]);
  }
}

  private recommendCountSource = new Subject<number>();
  recommendCount$ = this.recommendCountSource.asObservable();

  emitRecommendCount(count: number): void {
    this.recommendCountSource.next(count);
  }

  getRecommendCount(): Observable<number> {
    return this.recommendCount$;
  }

  private initialReRunLoader: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    setInitialReRunLoader(response: any) {
        this.initialReRunLoader.next(response);
    }

    getInitialReRunLoader(): Observable<any> {
        return this.initialReRunLoader.asObservable();
    };


// --- optional: reset stageList when leaving the project, alongside your existing clear() ---
// Add this line inside your existing clear() method if you want the list to reset
// when navigating away:
//   this.stageListSource.next([]);


  private revealContactCandidateSource = new BehaviorSubject<any>(null);
  revealContactCandidate$ = this.revealContactCandidateSource.asObservable();

  setRevealContactCandidate(data: any): void {
    this.revealContactCandidateSource.next(data);
  }

  clearRevealContactCandidate(): void {
    this.revealContactCandidateSource.next(null);
  }

  getRevealContactCandidate(): Observable<any> {
    return this.revealContactCandidateSource.asObservable();
  };

  /**
   * TRMS-2838: unlike export, the bulk-reveal scope picker AND its confirmation modal are
   * both handled entirely inside ProjectDetailsViewComponent (it already has everything it
   * needs via this service + AggregateService/PipelineFilterStoreService for the pipeline
   * "all pages" case), so no scope needs to travel to the routed child. This is just a
   * lightweight "a reveal was just submitted, refresh your revealed-status badges" signal -
   * deliberately separate from reload$, which also re-runs a heavier AI-sourcing-status poll
   * that a contact reveal has nothing to do with.
   */
  private revealSubmittedSource = new Subject<void>();
  revealSubmitted$ = this.revealSubmittedSource.asObservable();

  notifyRevealSubmitted(): void {
    this.revealSubmittedSource.next();
  }

  /**
   * Same-session backstop against re-submitting a reveal for candidates whose request
   * was just fired from here, for surfaces where the bulk status API doesn't (yet) return
   * a pending flag. Not authoritative across tabs/sessions - see fetchAndComputeRevealEligibility.
   */
  private justSubmittedRevealIds = new Set<string>();

  markRevealSubmitted(ids: string[]): void {
    (ids || []).forEach(id => id && this.justSubmittedRevealIds.add(id));
  }

  /**
   * Subset of justSubmittedRevealIds for which a FullEnrich job has been seen queued. Once such
   * an id is no longer pending, its reveal has finished (found something or not) - unlike a
   * plain submitted id, which may simply not have reached FullEnrich yet (bulk runs in the
   * background after responding).
   */
  private revealSeenPendingIds = new Set<string>();

  isRevealSubmitted(id: string): boolean {
    return !!id && this.justSubmittedRevealIds.has(id);
  }

  markRevealSeenPending(id: string): void {
    id && this.revealSeenPendingIds.add(id);
  }

  isRevealSeenPending(id: string): boolean {
    return !!id && this.revealSeenPendingIds.has(id);
  }

  /** The reveal for this id resolved (or failed without doing anything) - stop treating it as in flight. */
  clearRevealSubmitted(id: string): void {
    this.justSubmittedRevealIds.delete(id);
    this.revealSeenPendingIds.delete(id);
  }

  /**
   * Fetches enrichresults for `candidates` and returns a merged id -> enriched-item map.
   * Split out from fetchAndComputeRevealEligibility so a caller that needs breakdowns for
   * several overlapping subsets (e.g. Selected and Current page are always subsets of the
   * same loaded/candidate set - selection is pruned to loaded ids in setCandidateIds() -
   * and for Pipeline, Current page is itself a subset of "all pages" under the same filters)
   * can fetch the superset ONCE and bucket every subset locally, instead of one bulk
   * enrichresults call per scope.
   *
   * The backend hard-caps personids to 25 per call (recommendation.js enrichPersonData ->
   * 400 "Insufficient/Invalid parameters" above that), so for a superset larger than that -
   * only reachable via Pipeline's "all pages" - this chunks into batches of 25 and merges
   * the results, capping how many chunks run in parallel so a very large pipeline doesn't
   * fire dozens of requests at once.
   */
  fetchRevealStatusById(candidates: any[], idOf: (c: any) => string): Observable<Record<string, any>> {
    const personids = [...new Set(candidates.map(idOf).filter(Boolean))];
    if (!personids.length) {
      return of({});
    }

    const CHUNK_SIZE = 25;
    const CONCURRENCY = 5;
    const chunks: string[][] = [];
    for (let i = 0; i < personids.length; i += CHUNK_SIZE) {
      chunks.push(personids.slice(i, i + CHUNK_SIZE));
    }

    return from(chunks).pipe(
      mergeMap(chunk => this.hireSearchService.personSearchEnrichResults({ personids: chunk }), CONCURRENCY),
      toArray(),
      map((responses: any[]) => {
        const byId: Record<string, any> = {};
        responses.forEach((res: any) => {
          const results = res?.data?.results ?? [];
          results.forEach((item: any) => {
            [item?.elastic_id, item?.elasticid, item?.id, item?.idinindex]
              .filter(Boolean)
              .forEach((key) => { byId[key] = item; });
          });
        });
        return byId;
      })
    );
  }

  /**
   * Buckets `candidates` into eligible / alreadyRevealed / pending using an already-fetched
   * id -> enriched-item map (see fetchRevealStatusById). Pure/sync - no network call.
   *
   * Pending detection reads a pending flag off the same bulk response defensively
   * (pendingFullEnrich / isFullEnrichPending, matching the single-candidate preview
   * endpoint's naming) - a no-op today if the backend doesn't populate it yet, falling back
   * to the just-submitted set above.
   */
  computeRevealEligibility(
    candidates: any[],
    byId: Record<string, any>,
    idOf: (c: any) => string
  ): { eligible: any[]; alreadyRevealed: any[]; pending: any[] } {
    const eligible: any[] = [];
    const alreadyRevealed: any[] = [];
    const pending: any[] = [];

    candidates.forEach((candidate) => {
      const id = idOf(candidate);
      const enriched = byId[id];
      const revealed = !!enriched?.revealContactAttempted || !!enriched?.existingContactDetails?.length;
      const pendingFlag = !!enriched?.pendingFullEnrich || !!enriched?.isFullEnrichPending || this.justSubmittedRevealIds.has(id);

      if (revealed) {
        alreadyRevealed.push(candidate);
      } else if (pendingFlag) {
        pending.push(candidate);
      } else {
        eligible.push(candidate);
      }
    });

    return { eligible, alreadyRevealed, pending };
  }

  /**
   * Resolves exactly which of `candidates` are eligible for a fresh bulk reveal, as close
   * to the actual reveal operation as possible: fetches + buckets in one call. Convenience
   * wrapper over fetchRevealStatusById + computeRevealEligibility for a single subset - use
   * those two directly when computing breakdowns for multiple overlapping subsets at once.
   */
  fetchAndComputeRevealEligibility(
    candidates: any[],
    idOf: (c: any) => string
  ): Observable<{ eligible: any[]; alreadyRevealed: any[]; pending: any[] }> {
    if (!candidates.length) {
      return of({ eligible: [], alreadyRevealed: [], pending: [] });
    }
    return this.fetchRevealStatusById(candidates, idOf).pipe(
      map(byId => this.computeRevealEligibility(candidates, byId, idOf))
    );
  }

}
